import { useState } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import styles from "../../styles/portfolioManagerDashboard.module.scss";

const RADIAN = Math.PI / 180;
const EMPTY_COLOR = "#f0f0f0";

function ValueLabel({ cx, cy, midAngle, innerRadius, outerRadius, payload, fontSize }) {
  if (!payload?.displayValue && payload?.displayValue !== 0) return null;
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fontSize}
      fontWeight={700}
      fill="#fff"
    >
      {payload.displayValue}
    </text>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: "1px solid #eaebf0", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", whiteSpace: "nowrap" }}>
      <span style={{ color: "#424242" }}>{entry.name}: </span>
      <span style={{ color: payload[0].fill }}>{entry.range}%</span>
    </div>
  );
}

function GaugeChart({
  size = 200,
  showLegend = true,
  segments,
}) {
  const [hidden, setHidden] = useState(new Set());

  const toggleSegment = (name) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const visibleSegments = segments.filter((s) => !hidden.has(s.name));
  const totalValue = visibleSegments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  const isEmpty = totalValue === 0;

  // Radii/font sizes are proportional to `size` so the same component renders correctly from the
  // ~230px top-level donut down to a compact card-sized ring, instead of the fixed pixel offsets
  // this used to have (which go negative below ~180px).
  const cx = size / 2;
  const outerR = size * 0.4;
  const innerR = outerR * 0.67;
  const chartSize = size + 32;
  const centerFontSize = Math.max(14, Math.round(size * 0.13));
  const segmentFontSize = Math.max(8, Math.round(size * 0.045));

  // No real data (e.g. a group with zero risks) - show a plain empty ring rather than nothing,
  // so it still reads as "a pie chart with nothing in it" instead of a blank gap.
  const pieData = isEmpty
    ? [{ value: 1, name: "No data", range: 100 }]
    : visibleSegments.map((s) => ({ value: s.range, name: s.name, displayValue: s.value, range: s.range }));

  return (
    <div className={styles.gaugeWrapper}>
      <PieChart width={chartSize} height={chartSize} style={{ overflow: "visible" }}>
        <Pie
          data={pieData}
          cx={cx}
          cy={size / 2 + 10}
          startAngle={90}
          endAngle={-270}
          innerRadius={innerR}
          outerRadius={outerR}
          dataKey="value"
          stroke="none"
          paddingAngle={isEmpty ? 0 : 2}
          isAnimationActive={false}
          cornerRadius={isEmpty ? 0 : 4}
          labelLine={false}
          label={(props) => <ValueLabel {...props} fontSize={segmentFontSize} />}
        >
          {isEmpty ? (
            <Cell fill={EMPTY_COLOR} />
          ) : (
            visibleSegments.map((seg, i) => <Cell key={i} fill={seg.color} />)
          )}
        </Pie>
        {!isEmpty && <Tooltip content={<CustomTooltip />} />}
        {/* Center total */}
        <text x={cx + 5} y={size / 2 + 12} textAnchor="middle" dominantBaseline="central" fontSize={centerFontSize} fontWeight={700} fill="#342C82">
          {totalValue}
        </text>
      </PieChart>

      {showLegend && (
        <div className={styles.gaugeLegend}>
          {segments.map((seg, i) => {
            const isHidden = hidden.has(seg.name);
            return (
              <span
                key={i}
                className={styles.gaugeLegendItem}
                onClick={() => toggleSegment(seg.name)}
                style={{ cursor: "pointer", opacity: isHidden ? 0.35 : 1, userSelect: "none" }}
              >
                <span className={styles.gaugeLegendSwatch} style={{ background: seg.color }} />
                {seg.name}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default GaugeChart;
